/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * Columns of the row a record points at, offered to search and sort.
 *
 * A pass is searched by its event's title and ordered by its event's start,
 * neither of which the pass row holds. The server allows that for a column
 * named `<alias>.<column>` — `event.title` — when the view's Related Data has
 * an "up" source with that alias publishing that column (see the backend's
 * public_list_lookups.py). This lists exactly those, so the admin can only
 * offer what the server will honour.
 *
 * Pure: takes the stored related_config and the related-edges response, so it
 * is tested without rendering anything.
 */

export const TEXT_TYPES = ['string', 'text', 'varchar', 'char'];
export const DATE_TYPES = ['date', 'datetime'];

function sourcesOf(relatedConfig) {
  const rc = relatedConfig || {};
  return [...(rc.children || []), ...(rc.sources || [])];
}

function titleCase(name) {
  return String(name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * [{ name: 'event.title', label: 'Event › Title', data_type: 'text' }, …]
 *
 * Only "up" sources written as a relationship (no custom conditions), and only
 * their ticked columns — the same rules the server applies.
 */
export function lookupFieldOptions(relatedConfig, edges) {
  const out = [];
  const seen = new Set();
  for (const source of sourcesOf(relatedConfig)) {
    if (!source || source.dir !== 'up' || !source.via) continue;
    if (source.where && Object.keys(source.where).length) continue;
    const alias = source.as || source.table;
    if (!alias || seen.has(alias)) continue;
    seen.add(alias);

    const edge = (edges || []).find(e => e.table === source.table && e.via === source.via)
      || (edges || []).find(e => e.table === source.table);
    const columns = (edge && edge.fields) || [];

    for (const column of source.fields || []) {
      const meta = columns.find(c => c.name === column) || {};
      out.push({
        name: `${alias}.${column}`,
        label: `${titleCase(alias)} › ${meta.display_name || titleCase(column)}`,
        data_type: (meta.data_type || '').toLowerCase(),
      });
    }
  }
  return out;
}
